import { motion } from "framer-motion";
import { Target, Zap, Layers } from "lucide-react";

const CHAPTERS = [
  {
    n: "01",
    title: "Growth First",
    icon: Target,
    tag: "sky",
    text: "Every decision starts with one question: will this grow your business? If it won't move the number, we don't do it.",
  },
  {
    n: "02",
    title: "Strategy That Sells",
    icon: Zap,
    tag: "orange",
    text: "Sharp positioning, messaging and campaigns grounded in research — built to turn attention into pipeline.",
  },
  {
    n: "03",
    title: "Built to Scale",
    icon: Layers,
    tag: "sky",
    text: "We use targeted ads to reach your specific audience — so we attract the right clients and customers, not just more clicks.",
  },
];

export const Manifesto = () => (
  <section
    id="manifesto"
    data-testid="manifesto-section"
    className="relative mx-auto max-w-7xl px-6 py-28 md:py-40"
  >
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-kinetic"
    >
      The Manifesto
    </motion.p>
    <motion.h2
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="mb-16 max-w-3xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl md:mb-24 md:text-5xl"
    >
      Three ideas we are <span className="text-outline-orange">building on</span>
    </motion.h2>

    <div className="grid gap-6 md:grid-cols-3 md:gap-8">
      {CHAPTERS.map((c, i) => (
        <motion.article
          key={c.n}
          data-testid={`manifesto-chapter-${c.n}`}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -8 }}
          className="group rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow duration-300 hover:shadow-[0_20px_50px_rgb(255,90,31,0.08)] md:p-10"
        >
          <div className="mb-10 flex items-start justify-between">
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-kinetic md:text-sm">
              {c.n}
            </span>
            <c.icon
              size={22}
              className={`transition-colors duration-300 ${
                c.tag === "orange"
                  ? "text-slate-300 group-hover:text-kinetic"
                  : "text-slate-300 group-hover:text-skybrand"
              }`}
            />
          </div>
          <h3 className="mb-4 font-display text-xl font-bold text-slate-900 sm:text-2xl">
            {c.title}
          </h3>
          <p className="text-base leading-relaxed text-slate-600">{c.text}</p>
          <div
            className={`mt-8 h-0.5 w-10 transition-all duration-500 group-hover:w-full ${
              c.tag === "orange" ? "bg-kinetic" : "bg-skybrand"
            }`}
          />
        </motion.article>
      ))}
    </div>
  </section>
);
